/**
 * legFigures — the planner's ROAD figures for a trip, remembered on this
 * device so /saved shows the same distance the planner showed.
 *
 * Oct 2026: the same trip read 254 km in the planner and ~274 km on /saved.
 * The planner sums road-routed legs (Google road route at the rig's pace,
 * else the vehicle route); /saved had no road figures at all and recomputed
 * a straight-line estimate (haversine x ROAD_DETOUR_FACTOR 1.2), so any trip
 * whose roads are straighter than 1.2x read long, and windy ones read short.
 *
 * Keyed by the stops' COORDINATES in order, not stop ids: signing in moves a
 * trip to the server and every stop gets a new id, but the places (and so the
 * road figures) are the same. A changed stop list misses and /saved falls back
 * to the labelled estimate rather than showing figures for a different trip.
 */
import type { TripLeg, TripStop } from '../types/trip';

const STORAGE_KEY = 'trip-planner.legFigures.v1';
const MAX_TRIPS = 100;

export type RoadLegSource = 'vehicle' | 'google';

interface StoredFigures {
  source: RoadLegSource;
  legs: { distanceKm?: number; driveMinutes?: number; via?: string }[];
  at: number;
}

/** null when any stop lacks coordinates — no stable identity to key on. */
export function tripCoordKey(stops: TripStop[]): string | null {
  if (stops.length < 2) return null;
  const parts: string[] = [];
  for (const s of stops) {
    if (typeof s.lat !== 'number' || typeof s.lng !== 'number') return null;
    parts.push(`${s.lat.toFixed(4)},${s.lng.toFixed(4)}`);
  }
  return parts.join(';');
}

function readAll(): Record<string, StoredFigures> {
  try {
    if (typeof window === 'undefined') return {};
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeAll(all: Record<string, StoredFigures>): void {
  try {
    if (typeof window === 'undefined') return;
    const keys = Object.keys(all);
    if (keys.length > MAX_TRIPS) {
      keys
        .sort((a, b) => (all[a].at || 0) - (all[b].at || 0))
        .slice(0, keys.length - MAX_TRIPS)
        .forEach((k) => delete all[k]);
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* storage full / blocked: /saved falls back to the labelled estimate */
  }
}

/** Remember road figures for these stops (estimates are never stored). */
export function rememberLegFigures(stops: TripStop[], legs: TripLeg[], source: string): void {
  if (source !== 'vehicle' && source !== 'google') return;
  const key = tripCoordKey(stops);
  if (!key || legs.length !== stops.length - 1) return;
  if (!legs.every((l) => typeof l.distanceKm === 'number')) return;
  const all = readAll();
  all[key] = {
    source,
    legs: legs.map((l) => ({ distanceKm: l.distanceKm, driveMinutes: l.driveMinutes, via: l.via })),
    at: Date.now(),
  };
  writeAll(all);
}

/** The remembered road legs for exactly these stops, rebuilt on their ids. */
export function recallLegFigures(stops: TripStop[]): { legs: TripLeg[]; source: RoadLegSource } | null {
  const key = tripCoordKey(stops);
  if (!key) return null;
  const hit = readAll()[key];
  if (!hit || !Array.isArray(hit.legs) || hit.legs.length !== stops.length - 1) return null;
  const legs: TripLeg[] = hit.legs.map((l, i) => ({
    fromId: stops[i].id,
    toId: stops[i + 1].id,
    distanceKm: l.distanceKm,
    driveMinutes: l.driveMinutes,
    via: l.via,
    source: 'road',
  }));
  return { legs, source: hit.source };
}
